import React, { useEffect, useMemo, useState } from 'react'
import { Box, Grid, Stack, Typography, IconButton, CircularProgress } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import LoopIcon from '@mui/icons-material/Loop';
import DeleteIcon from 'common/customIcons/delete';
// utils and helpers
import { getData, getPermissionsByMode, hasSectionPermission, formatTimestampToWithoutTime } from 'utilities/common';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../redux/slices/modalSlice";
import { store } from "../../../redux/store";
import { openSnackbar } from "../../../redux/slices/common";
import { addDistributionManagement, deleteDistributionManagement, getDistributionConfig, getDistributionManagements } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import AgGridAccordionTable from "components/tables/AgGridAccordionTable";
import MDButton from 'components/MDButton';
import { showToastMsg } from "./addRegionKit";
import { DateFilter } from "common/filters";

const showError = (message, errortype) => {
  if (errortype === 'success') {
    store.dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
  }
  if (errortype === 'error') {
    store.dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
  }
};

const DistributionManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDistributing } = useSelector(state => state.distribution)

  const [customFilters, setCustomFilters] = useState({});
  const [refreshTable, setRefreshTable] = useState(false);
  const [distributionConfigs, setDistributionConfigs] = useState({});

  const columnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'Distribution Date',
        field: 'createdOn',
        suppressMenu: false,
        minWidth: 400,
        sortable: false,
        className: 'field-region',
        valueGetter: ({ data }) => `${formatTimestampToWithoutTime(data?.createdOn)}`
      },
      {
        headerName: 'Kit Value',
        field: 'wholesaleValue',
        suppressMenu: false,
        sortable: false,
        className: 'field-closing-hour',
        valueGetter: ({ data }) => data.wholesaleValue ? `$${data.wholesaleValue?.toLocaleString() || 0}` : 0
      },
      {
        headerName: 'Total No of Unique Products',
        field: 'totalProducts',
        suppressMenu: false,
        sortable: false,
        className: 'field-total-product',
      },
      {
        headerName: 'Total Product Quantities',
        field: 'totalQtyDistributed',
        suppressMenu: false,
        sortable: false,
        className: 'field-total-product',
      },
      {
        headerName: 'Status',
        field: 'status',
        suppressMenu: false,
        sortable: false,
        className: 'field-status',
      },
      {
        headerName: 'Actions',
        cellRenderer: ({ data = {} }) => {
          return <Box display="flex" flexDirection="row" justifyContent="end" gap={0.5}>
            {
              // data?.status === "Splitting Finished" &&
              <>
                <IconButton
                  disabled={!canView ? true : false}
                  onClick={() => navigate(`distribution-details/${data.distributionId}?distributionDate=${data?.createdOn}`)}
                  color='primary' size='small' variant='primary'
                >
                  <VisibilityIcon width='16px' height='16px' />
                </IconButton>
                <IconButton
                  disabled={!canDelete}
                  color='error'
                  size='small'
                  variant='primary'
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: data.distributionId }, modal: 'DELETE_MODAL' }))
                  }}
                >
                  <DeleteIcon width='16px' height='16px' />
                </IconButton>
              </>
            }
          </Box>
        },
        cellStyle: { textAlign: 'right', justifyContent: 'end' },
        headerClass: 'right-aligned-cell-header',
        maxWidth: 100,
        pinned: 'right',
        className: 'field-actions'
      },
    ];

    // if (distributionConfigs.isRetailer) {
    //     columns.splice(1, 0, {
    //         headerName: 'Retail Value',
    //         field: 'retailerValue',
    //         suppressMenu: false,
    //         sortable: false,
    //         className: 'field-opening-hour',
    //         valueGetter: ({ data }) => data.retailerValue ? `$${data.retailerValue?.toLocaleString() || 0}` : 0
    //     },)
    // }

    return columns;
  }, [distributionConfigs])

  const subRegionColumnDefs = [
    {
      headerName: 'Regions',
      field: 'region',
      suppressMenu: false,
      minWidth: 300,
      flex: 1.5,
      sortable: false
    },
    {
      headerName: 'Sub Regions',
      field: 'subRegions',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      valueGetter: ({ data = {} }) => {
        return data.subRegions?.length ? data.subRegions?.map(subRegion => subRegion?.subRegionName)?.join(", ") : "-"
      }
    },
    {
      headerName: 'Min Products Per Region',
      field: 'minProductPerRegion',
      suppressMenu: false,
      sortable: false,
      flex: 1.5,
      minWidth: 200
    },
    {
      headerName: 'Max Products Per Region',
      field: 'maxProductPerRegion',
      suppressMenu: false,
      sortable: false,
      cellStyle: { textAlign: "right", justifyContent: "end" },
      headerClass: "right-aligned-cell-header",
      flex: 1,
      minWidth: 200
    },
  ];

  const handleGetConfigs = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDistributionConfigs(data || {});
      }
    } catch (error) {
      console.log("error loading default value", error)
    }
  }

  const handleDateChange = async (values) => {
    setCustomFilters(prev => ({ ...prev, ...values }));
    setRefreshTable(prev => !prev)
  }

  const deleteHandler = async (id) => {
    try {
      dispatch(setModalLoader(true));
      const res = await dispatch(deleteDistributionManagement({ distributionId: id })).unwrap();
      showError(res?.message, 'success');
      dispatch(closeCustomModalDialog());
      setRefreshTable(!refreshTable)
    } catch (err) {
      dispatch(setModalLoader(false));
      showError(err?.message, 'error');
    };
  };

  const handleCreateDistribution = async () => {
    if (isDistributing) return;
    try {
      const response = await dispatch(addDistributionManagement({})).unwrap();
      if (response) {
        showToastMsg(response?.message, "success")
        setRefreshTable(prev => !prev)
      }
    } catch (error) {
      showToastMsg(error, "error")
    }
  }

  useEffect(() => {
    handleGetConfigs();
  }, [])

  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
  const canCreate = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Create");
  const canView = hasSectionPermission(permissionsForMode, "Inventory Distribution", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Delete");
  const canEdit = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Edit/Update");

  return (
    <CommonLayout>
      {
        canView &&
        <Box p={{ xs: 2, sm: 3, lg: 4 }}>
          {/* Top section */}
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant='h4' fontWeight="semibold">Distributions</Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2} className="btn-filter-icon">
                {/* Date Filter */}
                <DateFilter handleChange={handleDateChange} />
                {
                  (canCreate || canEdit) &&
                  <MDButton
                    style={{ height: "100%", minWidth: "160px", minHeight: "47px", gap: "6px" }}
                    variant="contained"
                    color="primary"
                    disabled={isDistributing}
                    onClick={handleCreateDistribution}
                  >
                    {isDistributing ? <CircularProgress size={20} color="light" /> : <><LoopIcon style={{ width: "22px", height: "22px" }} /> Distribute</>}
                  </MDButton>
                }

              </Stack>
            </Grid>
          </Grid>

          {/* Listing */}
          <div className="cus-distributions-table">
            <AgGridAccordionTable
              tableName="Distribution Management"
              columnDefs={columnDefs}
              expandedColumnDefs={subRegionColumnDefs}
              getDataMethod={getDistributionManagements}
              style={{ height: '70vh' }}
              customFilters={customFilters}
              refreshTable={refreshTable}
            />
          </div>
        </Box>
      }
    </CommonLayout>
  )
}

export default DistributionManagement